import { useRef } from 'react'
import { hsvToHex } from './colorUtils'

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n))
}

interface SaturationValuePadProps {
  /** Current hue in degrees (`0`-`360`) — controls which color the pad's gradient is built from. */
  hue: number
  saturation: number
  value: number
  onChange: (saturation: number, value: number) => void
  /** Accessible name (already translated) describing this 2D pad to screen readers. */
  label: string
}

/**
 * The saturation (x) / brightness (y) gradient square from a classic desktop color picker —
 * drag (mouse, touch, or pen, via Pointer Events) or use the arrow keys to pick a shade of the
 * current hue. Built from three stacked full-bleed gradients (base hue, white→transparent
 * left-to-right, black→transparent bottom-to-top) rather than a `<canvas>`, so it stays crisp
 * at any size and needs no per-frame redraw.
 */
export function SaturationValuePad({
  hue,
  saturation,
  value,
  onChange,
  label,
}: SaturationValuePadProps) {
  const padRef = useRef<HTMLDivElement>(null)

  function updateFromPointer(clientX: number, clientY: number) {
    const rect = padRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return
    const s = clamp01((clientX - rect.left) / rect.width)
    // Top of the pad is full brightness, so invert the vertical axis.
    const v = clamp01(1 - (clientY - rect.top) / rect.height)
    onChange(s, v)
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture(e.pointerId)
    updateFromPointer(e.clientX, e.clientY)
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    // `buttons === 0` covers the case where the button was released outside the element
    // right before capture kicked in.
    if (e.buttons === 0) return
    updateFromPointer(e.clientX, e.clientY)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 0.1 : 0.02
    switch (e.key) {
      case 'ArrowRight':
        onChange(clamp01(saturation + step), value)
        break
      case 'ArrowLeft':
        onChange(clamp01(saturation - step), value)
        break
      case 'ArrowUp':
        onChange(saturation, clamp01(value + step))
        break
      case 'ArrowDown':
        onChange(saturation, clamp01(value - step))
        break
      default:
        return
    }
    e.preventDefault()
  }

  return (
    <div
      ref={padRef}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(saturation * 100)}
      aria-valuetext={`${Math.round(saturation * 100)}% saturation, ${Math.round(value * 100)}% brightness`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onKeyDown={handleKeyDown}
      className="focus-visible:ring-pax-blue relative h-40 w-full cursor-crosshair touch-none rounded-xl select-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      style={{ backgroundColor: hsvToHex({ h: hue, s: 1, v: 1 }) }}
    >
      <div
        className="absolute inset-0 rounded-xl"
        style={{ backgroundImage: 'linear-gradient(to right, #fff, transparent)' }}
      />
      <div
        className="absolute inset-0 rounded-xl"
        style={{ backgroundImage: 'linear-gradient(to top, #000, transparent)' }}
      />
      <div
        aria-hidden="true"
        className="absolute h-4 w-4 -translate-x-1/2 translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
        style={{
          left: `${saturation * 100}%`,
          bottom: `${value * 100}%`,
          backgroundColor: hsvToHex({ h: hue, s: saturation, v: value }),
        }}
      />
    </div>
  )
}

interface HueSliderProps {
  /** Current hue in degrees (`0`-`360`). */
  hue: number
  onChange: (hue: number) => void
  /** Accessible name (already translated). */
  label: string
}

/**
 * Hue slider (the rainbow bar next to the saturation/brightness pad). A native
 * `<input type="range">` styled with a rainbow track — using the native element means
 * keyboard control, screen-reader announcements, and touch dragging all come for free.
 */
export function HueSlider({ hue, onChange, label }: HueSliderProps) {
  return (
    <input
      type="range"
      min={0}
      max={359}
      step={1}
      value={hue}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={label}
      className="[&::-moz-range-thumb]:border-pax-purple/40 [&::-webkit-slider-thumb]:border-pax-purple/40 h-3 w-full cursor-pointer appearance-none rounded-full outline-none [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow"
      style={{
        backgroundImage:
          'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
      }}
    />
  )
}
